export const GUIDE_GROUP_NAME = 'draw-facet-guides';
export const PREVIEW_DOT_NAME = 'draw-facet-preview-dot';

export type GuideType =
	| 'extension'
	| 'perp'
	| 'alignH'
	| 'alignV'
	| 'absAngle';

export interface Guide {
	origin: [number, number];
	angleDeg: number;
	type: GuideType;
	priority: number;
	opacity: number;
}

export interface GuideResult {
	guides: Guide[];
	snapped: { x: number; y: number } | null;
	// arc + label geometry at the anchor for the angle callout — null if degenerate
	angleCallout?: AngleCallout | null;
}

export interface GuideParams {
	cursor: { x: number; y: number };
	points: number[];
	zoomScale: number;
	angleTolDeg?: number;
	snapTol?: number;
	intersectTol?: number;
	isTouch?: boolean;
	// Snap only: other facets' corner nodes, for cross-facet alignment.
	externalNodes?: [number, number][];
}

const RAD = Math.PI / 180;
const SNAP_ANGLES_360 = [0, 45, 90, 135, 180, 225, 270, 315] as const;
const REL_ANGLES = [0, 45, 90, 135] as const; // 45° steps; 4 lines = 8 dirs
const LOCK_SWITCH_TOL = 5;
// shared by Guide and Snap so switching modes mid-gesture keeps the current locked angle
let lastLockAngle: number | null = null;

// call on a fresh Guide/Snap enable so the first pick isn't a stale held-over angle.
export function resetGuideLock() {
	lastLockAngle = null;
}

type Cand = { origin: [number, number]; angleDeg: number; type: GuideType; priority: number };

// normalize to a line angle [0, 180)
function normAngle(deg: number): number {
	let a = deg % 180;
	if (a < 0) a += 180;
	return a;
}

function angDiff(a: number, b: number): number {
	const d = Math.abs(a - b) % 360;
	return Math.min(d, 360 - d);
}

function perpDist(c: Cand, cx: number, cy: number): number {
	const dx = Math.cos(c.angleDeg * RAD);
	const dy = Math.sin(c.angleDeg * RAD);
	const vx = cx - c.origin[0], vy = cy - c.origin[1];
	const t = vx * dx + vy * dy;
	return Math.hypot(vx - t * dx, vy - t * dy);
}

function project(c: Cand, cx: number, cy: number): { x: number; y: number } {
	const dx = Math.cos(c.angleDeg * RAD);
	const dy = Math.sin(c.angleDeg * RAD);
	const t = (cx - c.origin[0]) * dx + (cy - c.origin[1]) * dy;
	return { x: c.origin[0] + t * dx, y: c.origin[1] + t * dy };
}

function lineIntersect(a: Cand, b: Cand): { x: number; y: number } | null {
	const d1x = Math.cos(a.angleDeg * RAD), d1y = Math.sin(a.angleDeg * RAD);
	const d2x = Math.cos(b.angleDeg * RAD), d2y = Math.sin(b.angleDeg * RAD);
	const denom = d1x * d2y - d1y * d2x;
	if (Math.abs(denom) < 1e-6) return null;
	const ox = b.origin[0] - a.origin[0], oy = b.origin[1] - a.origin[1];
	const t = (ox * d2y - oy * d2x) / denom;
	return { x: a.origin[0] + t * d1x, y: a.origin[1] + t * d1y };
}

export interface AngleCallout {
	angleDeg: number; // interior angle, 0-180, for the label text
	startDeg: number; // arc start (Konva Arc `rotation`)
	sweepDeg: number; // arc sweep (Konva Arc `angle`), equals angleDeg
}

// interior angle + arc geometry between rays `vertex->a` and `vertex->b`, for the angle callout.
function computeAngleCallout(vertex: [number, number], a: [number, number], b: [number, number]): AngleCallout | null {
	const v1x = a[0] - vertex[0], v1y = a[1] - vertex[1];
	const v2x = b[0] - vertex[0], v2y = b[1] - vertex[1];
	const m1 = Math.hypot(v1x, v1y), m2 = Math.hypot(v2x, v2y);
	if (m1 < 1e-6 || m2 < 1e-6) return null;
	const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (m1 * m2)));
	const angleDeg = (Math.acos(cos) * 180) / Math.PI;
	// near-0° only happens retracing back over the previous segment (not a real corner) — skip it
	if (angleDeg < 10) return null;

	const a1 = (Math.atan2(v1y, v1x) * 180) / Math.PI;
	const a2 = (Math.atan2(v2y, v2x) * 180) / Math.PI;
	const diff = ((a2 - a1) % 360 + 360) % 360;
	const startDeg = diff <= 180 ? a1 : a2;
	const sweepDeg = diff <= 180 ? diff : 360 - diff;
	return { angleDeg, startDeg, sweepDeg };
}

function relTypeFor(step: number): GuideType {
	if (step === 0) return 'extension';
	if (step === 90) return 'perp';
	return 'absAngle';
}

// smallest angle between two lines (0..90)
function lineAngDist(a: number, b: number): number {
	const d = Math.abs(normAngle(a) - normAngle(b)) % 180;
	return Math.min(d, 180 - d);
}

// TOUCH: snap the point to the nearest intersection of angle guides
function computeIntersectionSnapGuides(params: GuideParams): GuideResult {
	const { cursor, points, zoomScale } = params;
	const scale = Math.max(zoomScale, 1e-6);
	const ANGLE_TOL = params.angleTolDeg ?? 8;
	const tol = (params.snapTol ?? 15) / scale;
	const iTol = (params.intersectTol ?? 10) / scale;

	const n = Math.floor(points.length / 2);
	if (n < 2) return { guides: [], snapped: null };

	const verts: [number, number][] = Array.from({ length: n }, (_, i) => [points[2 * i], points[2 * i + 1]]);
	const anchor = verts[n - 1];
	const { x: cx, y: cy } = cursor;

	const candidates: Cand[] = [];

	const prev = verts[n - 2];
	const lastDx = anchor[0] - prev[0], lastDy = anchor[1] - prev[1];
	if (Math.hypot(lastDx, lastDy) > 1 / scale) {
		const lastAngle = normAngle(Math.atan2(lastDy, lastDx) / RAD);
		candidates.push({ origin: anchor, angleDeg: lastAngle, type: 'extension', priority: 4 });
		candidates.push({ origin: anchor, angleDeg: normAngle(lastAngle + 90), type: 'perp', priority: 3 });
	}

	for (const v of verts) {
		const dx = cx - v[0], dy = cy - v[1];
		if (Math.hypot(dx, dy) < 2 / scale) continue;

		const rawAngle = ((Math.atan2(dy, dx) / RAD) + 360) % 360;

		let bestSnap = -1, bestDiff = Infinity;
		for (const sa of SNAP_ANGLES_360) {
			const d = angDiff(rawAngle, sa);
			if (d < bestDiff) { bestDiff = d; bestSnap = sa; }
		}
		if (bestDiff > ANGLE_TOL) continue;

		const guideAngle = normAngle(bestSnap);
		let type: GuideType = 'absAngle';
		if (guideAngle === 0) type = 'alignH';
		else if (guideAngle === 90) type = 'alignV';

		candidates.push({ origin: v, angleDeg: guideAngle, type, priority: v === anchor ? 3 : 2 });
	}

	if (!candidates.length) return { guides: [], snapped: null };

	const byKey = new Map<string, Cand>();
	for (const c of candidates) {
		const key = `${Math.round(c.origin[0])},${Math.round(c.origin[1])}|${Math.round(c.angleDeg)}`;
		const ex = byKey.get(key);
		if (!ex || c.priority > ex.priority) byKey.set(key, c);
	}

	const sorted = Array.from(byKey.values()).sort((a, b) => b.priority - a.priority);
	const unique: Cand[] = [];
	for (const g of sorted) {
		const dup = unique.some(k => {
			if (Math.hypot(k.origin[0] - g.origin[0], k.origin[1] - g.origin[1]) > 2) return false;
			const diff = Math.abs(k.angleDeg - g.angleDeg);
			return Math.min(diff, 180 - diff) < 8;
		});
		if (!dup) unique.push(g);
	}

	const snapCands = unique
		.map(c => ({ c, d: perpDist(c, cx, cy) }))
		.filter(({ d }) => d <= tol)
		.sort((a, b) => b.c.priority - a.c.priority || a.d - b.d)
		.map(({ c }) => c);

	if (!snapCands.length) return { guides: [], snapped: null };

	let snapped: { x: number; y: number } | null = null;
	let bestD = iTol;
	let snapI = 0, snapJ = -1;

	for (let i = 0; i < snapCands.length; i++) {
		for (let j = i + 1; j < snapCands.length; j++) {
			const p = lineIntersect(snapCands[i], snapCands[j]);
			if (!p) continue;
			const d = Math.hypot(p.x - cx, p.y - cy);
			if (d < bestD) { bestD = d; snapped = p; snapI = i; snapJ = j; }
		}
	}
	if (!snapped) {
		snapped = project(snapCands[0], cx, cy);
		snapI = 0;
		snapJ = -1;
	}

	// Only emit guides that produced the snap — no extras.
	const activeIdx = snapJ >= 0 ? [snapI, snapJ] : [snapI];
	const guides: Guide[] = activeIdx.map(i => ({
		origin: snapCands[i].origin,
		angleDeg: snapCands[i].angleDeg,
		type: snapCands[i].type,
		priority: snapCands[i].priority,
		opacity: 1.0,
	}));

	return { guides, snapped };
}

// DESKTOP: lock preview to the relative-angle line (0/45/90/135°) nearest the cursor
function computeAngleLockGuides(params: GuideParams): GuideResult {
	const { cursor, points, zoomScale } = params;
	const scale = Math.max(zoomScale, 1e-6);

	const n = Math.floor(points.length / 2);
	if (n < 2) { lastLockAngle = null; return { guides: [], snapped: null }; }

	const verts: [number, number][] = Array.from({ length: n }, (_, i) => [points[2 * i], points[2 * i + 1]]);
	const anchor = verts[n - 1];
	const prev = verts[n - 2];
	const { x: cx, y: cy } = cursor;

	// degenerate prev segment => freeform
	const lastDx = anchor[0] - prev[0], lastDy = anchor[1] - prev[1];
	if (Math.hypot(lastDx, lastDy) <= 1 / scale) { lastLockAngle = null; return { guides: [], snapped: null }; }
	const lastAngle = Math.atan2(lastDy, lastDx) / RAD;

	const cands = REL_ANGLES.map(step => {
		const angleDeg = normAngle(lastAngle + step);
		const dx = Math.cos(angleDeg * RAD), dy = Math.sin(angleDeg * RAD);
		return { origin: anchor, angleDeg, type: relTypeFor(step), dx, dy };
	});

	// sticky lock — holds until cursor nears another candidate (reset via resetGuideLock on fresh enable)
	const cursorAngle = Math.atan2(cy - anchor[1], cx - anchor[0]) / RAD;
	let active = cands[0], nearestDist = Infinity;
	for (const c of cands) {
		const d = lineAngDist(cursorAngle, c.angleDeg);
		if (d < nearestDist) { nearestDist = d; active = c; }
	}
	if (lastLockAngle != null && nearestDist > LOCK_SWITCH_TOL) {
		const held = cands.find(c => lineAngDist(c.angleDeg, lastLockAngle as number) < 1);
		if (held) active = held;
	}
	lastLockAngle = active.angleDeg;

	const ax = anchor[0], ay = anchor[1];
	const tProj = (cx - ax) * active.dx + (cy - ay) * active.dy;
	const snapped = { x: ax + tProj * active.dx, y: ay + tProj * active.dy };

	return {
		guides: [{ origin: active.origin, angleDeg: active.angleDeg, type: active.type, priority: 4, opacity: 1.0 }],
		snapped,
		angleCallout: computeAngleCallout(anchor, prev, [snapped.x, snapped.y]),
	};
}

export function computeDrawFacetGuides(params: GuideParams): GuideResult {
	return params.isTouch ? computeIntersectionSnapGuides(params) : computeAngleLockGuides(params);
}

export interface SnapGuideResult {
	// where the next point lands (projection on the chosen line, possibly node-aligned)
	snapped: { x: number; y: number };
	// infinite constraint line through the anchor (the previous point)
	constraint: { origin: [number, number]; angleDeg: number };
	// finite alignment line from the snapped point to the reference node, when node-aligned
	snapSegment: { from: [number, number]; to: [number, number] } | null;
	// arc + label geometry at the anchor for the angle callout — null if degenerate
	angleCallout: AngleCallout | null;
}

// SNAP (Shift + S): constrain the next segment to the relative-angle line nearest the
// cursor (0/45/90/135 to the previous segment — same angles as Guide, so it doesn't jump),
// then snap the point to positions that stay aligned with existing polygon nodes.
// Falls back to the plain projection on that line when no node is within the threshold.
export function computeRelativeOrthogonalSnap(params: GuideParams): SnapGuideResult | null {
	const { cursor, points, zoomScale, externalNodes } = params;
	const scale = Math.max(zoomScale, 1e-6);
	// light catch radius — snap only when the cursor is essentially on the alignment (like Guide)
	const tol = 10 / scale;

	const n = Math.floor(points.length / 2);
	if (n < 2) { lastLockAngle = null; return null; }

	const verts: [number, number][] = Array.from({ length: n }, (_, i) => [points[2 * i], points[2 * i + 1]]);
	const B = verts[n - 1];
	const prev = verts[n - 2];

	// degenerate prev segment => no constraint
	const lastDx = B[0] - prev[0], lastDy = B[1] - prev[1];
	if (Math.hypot(lastDx, lastDy) <= 1 / scale) { lastLockAngle = null; return null; }
	const baseAngle = Math.atan2(lastDy, lastDx) / RAD;

	// constraint = 0/45/90/135 line nearest cursor, matching Guide — sticky lock, same as Guide
	const cursorAngle = Math.atan2(cursor.y - B[1], cursor.x - B[0]) / RAD;
	let chosenAngle = baseAngle + 90;
	let nearestDist = Infinity;
	for (const step of REL_ANGLES) {
		const a = baseAngle + step;
		const d = lineAngDist(cursorAngle, a);
		if (d < nearestDist) { nearestDist = d; chosenAngle = a; }
	}
	if (lastLockAngle != null && nearestDist > LOCK_SWITCH_TOL) {
		const held = REL_ANGLES.map((step) => baseAngle + step).find((a) => lineAngDist(a, lastLockAngle as number) < 1);
		if (held != null) chosenAngle = held;
	}
	lastLockAngle = chosenAngle;

	const constraint: Cand = { origin: B, angleDeg: chosenAngle, type: 'perp', priority: 0 };
	const proj = project(constraint, cursor.x, cursor.y);
	let snap = proj;
	let refNode: [number, number] | null = null;
	let bestDist = tol;
	// this facet's earlier vertices, plus corner nodes from other facets (externalNodes)
	const refCandidates = [...verts.slice(0, n - 1), ...(externalNodes ?? [])];
	for (const refPt of refCandidates) {
		// keep the constraint-line intersection nearest the projection, across all 4 angles
		for (const step of REL_ANGLES) {
			const cand = lineIntersect(constraint, { origin: refPt, angleDeg: baseAngle + step, type: 'extension', priority: 0 });
			if (!cand) continue; // node line parallel to the constraint line => no crossing
			// skip candidates that collapse onto the anchor
			if (Math.hypot(cand.x - B[0], cand.y - B[1]) < 1 / scale) continue;
			const d = Math.hypot(cand.x - proj.x, cand.y - proj.y);
			if (d < bestDist) { bestDist = d; snap = cand; refNode = refPt; }
		}
	}

	return {
		snapped: snap,
		constraint: { origin: B, angleDeg: chosenAngle },
		snapSegment: refNode ? { from: [snap.x, snap.y], to: refNode } : null,
		angleCallout: computeAngleCallout(B, prev, [snap.x, snap.y]),
	};
}
